import React, { useMemo, useState } from 'react'
import { Controller, FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form'
import { Box, TextField, Typography } from '@mui/material'

import { useAppDispatch, useAppSelector } from '#/app/store/store'
import { CompanyData } from '#/features/register-business/api/get-company-date/types'
import { useCheckTypeCompany } from '#/features/register-business/model/legal-info/use-check-type-company'
import { getStepperInputStyles } from '#/features/register-business/ui/shared/form-styles'
import styles from '#/features/register-business/ui/step-information/step-information.module.scss'
import { IEmailForms } from '#/features/register-by-email/model/types'
import { ButtonUI } from '#/shared'
import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'

import { CompanyAutocomplete } from '../../api/company-autocomplete'
import { companyNameOptions } from '../../lib/constatnts-step-legal-informative'
import { useAutoSetInfo } from '../../model/legal-info/use-auto-set-info'
import { setCompanyName, setInn, setOgrn, switchNextStep } from '../../model/stepper-slice'

export const StepLegalInformative = () => {
	const dispatch = useAppDispatch()
	const { companyName, ogrn, inn } = useAppSelector((state) => state.stepper.dataForCreate)

	const { showMessage } = useShowDataStore()

	const [selectedCompany, setSelectedCompany] = useState<CompanyData | null | string>(null)

	const methods = useForm({
		defaultValues: {
			companyName,
			inn,
			ogrn,
		},
	})

	const {
		control,
		handleSubmit,
		formState: { errors },
		register,
	} = methods

	const [rulesData, isIP] = useCheckTypeCompany(typeof selectedCompany === 'string' ? selectedCompany : selectedCompany?.value)

	useAutoSetInfo(methods, selectedCompany)

	const onSubmit = (data: any) => {
		dispatch(setCompanyName(data.companyName))
		dispatch(setInn(data.inn))
		dispatch(setOgrn(data.ogrn))
		dispatch(switchNextStep())
	}

	const handleError: SubmitErrorHandler<IEmailForms> = (formErrors) => {
		showMessage(Object.values(formErrors)[0].message || 'Неверные данные')
	}

	const innRules = useMemo(() => rulesData.inn, [rulesData])
	const ogrnRules = useMemo(() => rulesData.ogrn, [rulesData])

	const autoCompleteSx = {
		...getStepperInputStyles(!!errors.companyName),
		'.MuiAutocomplete-inputRoot': {
		  padding: '0 !important',
		},
		'.MuiAutocomplete-input': {
		  padding: '13px 16px !important',
		},
	  }

	return (
		<FormProvider {...methods}>
			<form id='step2' className={styles.form} onSubmit={handleSubmit(onSubmit, handleError)}>
				<Box className={styles.formInner}>
					<Box className={styles.element}>
						<Typography>Наименование вашей организации</Typography>
						<Controller
							name='companyName'
							control={control}
							rules={companyNameOptions}
							render={({ field }) => (
								<CompanyAutocomplete
									{...field}
									methods={methods}
									fieldName='companyName'
									validationRules={companyNameOptions}
									inModal={false}
									textFieldSx={autoCompleteSx}
									defaultValue={companyName}
									error={errors.companyName}
									onCompanySelect={(value) => {
										setSelectedCompany(value)
										field.onChange(typeof value === 'string' ? value : value?.value ?? '')
									}}
								/>
							)}
						/>
					</Box>

					<Box className={styles.element}>
						<Typography>ИНН</Typography>
						<TextField
							fullWidth
							sx={getStepperInputStyles(!!errors.inn)}
							error={!!errors.inn}
							helperText={errors.inn?.message}
							{...register('inn', innRules)}
						/>
					</Box>

					<Box className={styles.element}>
						<Typography>{isIP ? 'ОГРНИП' : 'ОГРН'}</Typography>
						<TextField
							fullWidth
							sx={getStepperInputStyles(!!errors.ogrn)}
							error={!!errors.ogrn}
							helperText={errors.ogrn?.message}
							{...register('ogrn', ogrnRules)}
						/>
					</Box>

					<Box className={styles.btn} display='flex' justifyContent='center'>
						<Box paddingRight={1}>
							<ButtonUI onClick={() => dispatch(switchNextStep())} text='Пропустить' />
						</Box>
						<Box>
							<ButtonUI onClick={handleSubmit(onSubmit, handleError)} text='Продолжить' />
						</Box>
					</Box>
				</Box>
			</form>
		</FormProvider>
	)
}
